/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : GuideViewerPage
 *   Feature      : 195-universal-logging-onboarding
 *   Description  : In-app markdown reference viewer for a platform guide, with
 *                  a "Download .md" action. Guides are registered in
 *                  data/help/platformGuides.ts and served from public/docs/.
 *   Blueprint    : docs/design-blueprint.md (Constitution XLV — kit + tokens)
 * ============================================================================
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BookOpen, Download, ArrowLeft } from 'lucide-react';
import {
  PageLayout,
  Card,
  Button,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/ui';
import { findGuide } from '../../data/help/platformGuides';

/**
 * Token-driven renderers for the markdown elements. We deliberately do NOT use a
 * `prose` plugin — every element maps to design-token classes so the guide reads
 * identically in light + dark mode (Constitution XLV).
 */
const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="mb-4 mt-6 text-2xl font-bold text-[var(--color-text-primary)] first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-3 mt-6 border-b border-[var(--color-border)] pb-1 text-xl font-semibold text-[var(--color-text-primary)]">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-2 mt-5 text-lg font-semibold text-[var(--color-text-primary)]">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="mb-2 mt-4 text-base font-semibold text-[var(--color-text-primary)]">
      {children}
    </h4>
  ),
  p: ({ children }) => (
    <p className="mb-4 leading-relaxed text-[var(--color-text-secondary)]">{children}</p>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      className="text-[var(--color-accent)] hover:underline"
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="mb-4 list-disc space-y-1 pl-6 text-[var(--color-text-secondary)]">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-4 list-decimal space-y-1 pl-6 text-[var(--color-text-secondary)]">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  code: ({ className, children }) => {
    // Inline code (no language class) vs. fenced block child.
    const isBlock = typeof className === 'string' && className.includes('language-');
    if (isBlock) {
      return (
        <code className="block whitespace-pre font-mono text-sm text-[var(--color-text-primary)]">
          {children}
        </code>
      );
    }
    return (
      <code className="rounded border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-1.5 py-0.5 font-mono text-sm text-[var(--color-text-primary)]">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="mb-4 overflow-x-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-4 text-sm">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="mb-4 overflow-x-auto">
      <table className="w-full border-collapse border border-[var(--color-border)] text-sm">
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2 text-left font-semibold text-[var(--color-text-primary)]">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-[var(--color-border)] px-3 py-2 text-[var(--color-text-secondary)]">
      {children}
    </td>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-4 border-l-4 border-[var(--color-accent)] bg-[var(--color-bg-subtle)] py-2 pl-4 pr-2 text-[var(--color-text-secondary)]">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-6 border-[var(--color-border)]" />,
};

const BackLink: React.FC = () => (
  <Link
    to="/help/docs"
    className="inline-flex items-center gap-1 text-sm font-medium text-[var(--color-accent)] hover:underline"
  >
    <ArrowLeft className="h-4 w-4" />
    Back to Documentation
  </Link>
);

export const GuideViewerPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const guide = findGuide(slug);

  const [markdown, setMarkdown] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!guide) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setMarkdown(null);

    // Revalidate against the server — the served .md can change without its filename
    // changing (a guide's content is updated in place), so a cached copy must not be
    // served stale.
    fetch(`/docs/${guide.file}`, { cache: 'no-cache' })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load guide (HTTP ${res.status})`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) {
          setMarkdown(text);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load guide');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [guide]);

  // Unknown slug — no such guide.
  if (!guide) {
    return (
      <PageLayout title="Guide Not Found" icon={<BookOpen className="h-6 w-6" />}>
        <Card>
          <EmptyState
            title="Guide not found"
            message="The guide you are looking for does not exist or has been moved."
            icon={<BookOpen className="h-10 w-10" />}
            action={<BackLink />}
          />
        </Card>
      </PageLayout>
    );
  }

  const downloadHref = `/docs/${guide.file}`;
  // Platform Guides are downloadable (meant to be pulled into other codebases); user
  // guides are read in-app only. Default to downloadable when the flag is unset.
  const isDownloadable = guide.downloadable !== false;

  return (
    <PageLayout
      title={guide.title}
      description={guide.description}
      icon={<BookOpen className="h-6 w-6" />}
      actions={
        isDownloadable ? (
          <a href={downloadHref} download>
            <Button variant="secondary" size="sm" leftIcon={<Download className="h-4 w-4" />}>
              Download .md
            </Button>
          </a>
        ) : undefined
      }
    >
      <div className="mb-4">
        <BackLink />
      </div>

      <Card>
        {loading && <LoadingState rows={8} />}

        {!loading && loadError && (
          <ErrorState
            title="Could not load this guide"
            message={loadError}
            onRetry={() => {
              // Re-trigger the effect by clearing then re-fetching.
              setLoading(true);
              setLoadError(null);
              fetch(downloadHref)
                .then((res) => {
                  if (!res.ok) throw new Error(`Failed to load guide (HTTP ${res.status})`);
                  return res.text();
                })
                .then((text) => {
                  setMarkdown(text);
                  setLoading(false);
                })
                .catch((err: unknown) => {
                  setLoadError(err instanceof Error ? err.message : 'Failed to load guide');
                  setLoading(false);
                });
            }}
          />
        )}

        {!loading && !loadError && markdown != null && (
          <div className="max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {markdown}
            </ReactMarkdown>
          </div>
        )}
      </Card>
    </PageLayout>
  );
};

export default GuideViewerPage;
